import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cotizacionService } from '../services/cotizacionService';
import type { CotizacionDolar } from '../types';
import { formatearMonto } from '../utils/formatters';

type Lado = 'compra' | 'venta';

interface ConsultarCotizacionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Recibe el valor elegido y la cotización de la que sale. */
  onConfirm: (valor: number, cotizacion: CotizacionDolar) => Promise<void> | void;
  isGuardando?: boolean;
}

export const COTIZACIONES_DOLAR_QUERY_KEY = ['cotizaciones', 'dolar'];

const formatearFechaHora = (fecha: string | null): string => {
  if (!fecha) return 'sin fecha';

  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(fecha)
  );
};

/**
 * Consulta las cotizaciones del dólar (oficial, MEP y blue) para elegir el
 * valor del presupuesto. Si la API falla, se puede seguir cargando a mano.
 */
export const ConsultarCotizacionDialog: React.FC<ConsultarCotizacionDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isGuardando = false,
}) => {
  const [seleccion, setSeleccion] = useState<{ tipo: string; lado: Lado } | null>(null);

  const { data: cotizaciones = [], isLoading, isError, error } = useQuery({
    queryKey: COTIZACIONES_DOLAR_QUERY_KEY,
    queryFn: cotizacionService.getCotizacionesDolar,
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (open) setSeleccion(null);
  }, [open]);

  const cotizacionElegida = cotizaciones.find((cotizacion) => cotizacion.tipo === seleccion?.tipo);
  const valorElegido = cotizacionElegida && seleccion ? cotizacionElegida[seleccion.lado] : null;

  const mensajeError =
    (error as any)?.response?.data?.message ??
    'No se pudo consultar la cotización del dólar. Puede cargarla a mano.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Consultar cotización del dólar</DialogTitle>
          <DialogDescription>
            Elegí el valor a usar en el presupuesto. Reemplaza la cotización cargada.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Consultando cotizaciones…
          </div>
        )}

        {isError && (
          <p role="alert" className="py-2 text-sm text-destructive">
            {mensajeError}
          </p>
        )}

        {!isLoading && !isError && (
          <div className="space-y-3">
            {cotizaciones.map((cotizacion) => (
              <fieldset key={cotizacion.tipo} className="rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">{cotizacion.nombre}</legend>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {(['compra', 'venta'] as Lado[]).map((lado) => {
                    const valor = cotizacion[lado];
                    if (valor === null) return null;

                    return (
                      <label key={lado} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="cotizacion-dolar"
                          checked={seleccion?.tipo === cotizacion.tipo && seleccion.lado === lado}
                          onChange={() => setSeleccion({ tipo: cotizacion.tipo, lado })}
                          aria-label={`${cotizacion.nombre} ${lado} ${formatearMonto(valor, 'ARS')}`}
                        />
                        <span className="capitalize">{lado}</span>
                        <span className="font-medium">{formatearMonto(valor, 'ARS')}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Actualizado: {formatearFechaHora(cotizacion.fechaActualizacion)}
                </p>
              </fieldset>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGuardando}>
            Cancelar
          </Button>
          <Button
            onClick={() => cotizacionElegida && valorElegido !== null && void onConfirm(valorElegido, cotizacionElegida)}
            disabled={valorElegido === null || isGuardando}
          >
            Usar este valor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
